import { Component, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { UserService } from '../../../core/services/user.service';

@Component({
    selector: 'app-user-form',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, RouterLink],
    templateUrl: './user-form.component.html',
    styleUrl: './user-form.component.scss'
})
export class UserFormComponent implements OnInit {
    private fb = inject(FormBuilder);
    private userService = inject(UserService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private cdr = inject(ChangeDetectorRef);

    userForm = this.fb.group({
        email: ['', [Validators.required, Validators.email]],
        password: [''],
        firstName: [''],
        lastName: [''],
        isActive: [true],
        role: ['', Validators.required],
        schedules: this.fb.array([])
    });

    availableRoles: string[] = [];
    daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    isEditMode = false;
    userId: string | null = null;
    isLoading = false;
    errorMessage = '';

    ngOnInit() {
        this.initSchedules();
        this.loadRoles();
        this.userId = this.route.snapshot.paramMap.get('id');
        if (this.userId) {
            this.isEditMode = true;
            this.loadUser(this.userId);
        } else {
            this.userForm.get('password')?.addValidators([Validators.required, Validators.minLength(6)]);
            this.userForm.get('password')?.updateValueAndValidity();
        }
    }

    initSchedules() {
        const schedules = this.userForm.get('schedules') as any; // Cast to avoid type issues if strict
        schedules.clear();
        // Create 7 days (0=Sunday to 6=Saturday)
        for (let i = 0; i < 7; i++) {
            schedules.push(this.fb.group({
                dayOfWeek: [i],
                isWorkingDay: [i >= 1 && i <= 5], // Default Mon-Fri working
                startTime: ['09:00'],
                endTime: ['18:00']
            }));
        }
    }

    get schedulesArray() {
        return this.userForm.get('schedules') as any;
    }

    loadRoles() {
        this.userService.getRoles().subscribe({
            next: (data: any) => {
                console.log('Roles API response:', data);
                let items = data;
                if (!Array.isArray(data)) {
                    items = data?.data || data?.items || data?.result || [];
                }
                if (Array.isArray(items)) {
                    this.availableRoles = items
                        .map((r: any) => {
                            if (typeof r === 'string') return r;
                            return r.name || r.roleName || r.title || 'Unknown';
                        })
                        .filter((r: string) => r !== 'SystemAdmin' && r !== 'Unknown');
                    this.cdr.detectChanges();
                } else {
                    this.availableRoles = [];
                }
            },
            error: (err) => {
                console.error('Failed to load roles', err);
                this.availableRoles = [];
            }
        });
    }

    loadUser(id: string) {
        this.isLoading = true;
        this.userService.getAll().subscribe({
            next: (users: any[]) => {
                const items = Array.isArray(users) ? users : ((users as any)?.data || (users as any)?.items || []);
                const user = items.find((u: any) => u.id === id);

                if (user) {
                    this.userForm.patchValue({
                        email: user.email,
                        firstName: user.firstName,
                        lastName: user.lastName,
                        isActive: user.isActive ?? true,
                        role: user.roles && user.roles.length > 0 ? user.roles[0] : ''
                    });

                    // Load schedules if available
                    if (user.schedules && Array.isArray(user.schedules)) {
                        const formArray = this.userForm.get('schedules') as any;
                        user.schedules.forEach((s: any) => {
                             // Find the control for this day
                             const control = formArray.controls.find((c: any) => c.get('dayOfWeek').value === s.dayOfWeek);
                             if (control) {
                                 control.patchValue({
                                     isWorkingDay: s.isWorkingDay,
                                     startTime: s.startTime ? s.startTime.substring(0, 5) : '09:00',
                                     endTime: s.endTime ? s.endTime.substring(0, 5) : '18:00'
                                 });
                             }
                        });
                    }
                }
                this.isLoading = false;
            },
            error: (err) => {
                console.error(err);
                this.isLoading = false;
                this.errorMessage = 'Failed to load user.';
            }
        });
    }

    onSubmit() {
        if (this.userForm.valid) {
            this.isLoading = true;
            this.errorMessage = '';
            const formValue = this.userForm.value;

            const userData = {
                email: formValue.email,
                password: formValue.password,
                firstName: formValue.firstName || '',
                lastName: formValue.lastName || '',
                status: formValue.isActive ? 1 : 2,
                roles: formValue.role ? [formValue.role] : [],
                schedules: formValue.schedules
            };

            const roles = formValue.role ? [formValue.role] : [];

            const request = this.isEditMode
                ? this.userService.update(this.userId!, userData)
                : this.userService.create(userData);

            request.subscribe({
                next: (res: any) => {
                    const targetId = this.isEditMode ? this.userId! : (res?.id || res?.data?.id);
                    if (targetId && roles.length > 0) {
                        this.userService.changeRoles(targetId, roles).subscribe({
                            next: () => {
                                this.isLoading = false;
                                this.router.navigate(['/users']);
                            },
                            error: (err) => {
                                console.error('User saved but roles failed', err);
                                this.isLoading = false;
                                this.router.navigate(['/users']);
                            }
                        });
                    } else {
                        this.isLoading = false;
                        this.router.navigate(['/users']);
                    }
                },
                error: (err) => {
                    this.isLoading = false;
                    this.errorMessage = 'Operation failed. Please try again.';
                    console.error(err);
                }
            });
        }
    }
}
